import { useState, useEffect } from 'react';
import { t } from 'i18n';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from 'context/ThemeContext';
import api from 'services/api';

const MOIS = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];
const OFFRE_COLORS = { starter: 'var(--gss-success)', business: 'var(--gss-info)', enterprise: 'var(--gss-red)' };
const OFFRE_LABELS = { starter: 'ALPHA', business: 'VANGUARD', enterprise: 'ELITE' };
const STATUT_COLORS = { actif: 'var(--gss-success)', suspendu: 'var(--gss-danger)', essai: 'var(--gss-warning)' };
const ALERTE_CFG = {
  impaye_30j: { color: 'var(--gss-danger)', bg: 'var(--gss-red-glow)', label: 'Impaye > 30j' },
  essai_expire: { color: 'var(--gss-warning)', bg: 'rgba(217, 119, 6, 0.08)', label: 'Essai expire bientot' },
  suspendu: { color: 'var(--gss-text-light)', bg: 'var(--gss-border)', label: 'Suspendu' },
};

function saApi() {
  const token = localStorage.getItem('gss_sa_token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

export default function SuperAdminDashboard() {
  const nav = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demandesNouv, setDemandesNouv] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('gss_sa_token');
    if (!token) { nav('/super-admin/login'); return; }
    Promise.all([
      api.get('/super-admin/dashboard', saApi()),
      api.get('/super-admin/demandes-demo', saApi()),
    ]).then(([d, dem]) => {
      setData(d.data);
      setDemandesNouv(dem.data.filter(x => x.statut === 'nouveau').length);
    }).catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem('gss_sa_token');
          nav('/super-admin/login');
        }
      }).finally(() => setLoading(false));
  }, [nav]);

  if (loading) return <div style={S.page}><p style={{ color: 'var(--gss-text-light)' }}>Chargement...</p></div>;
  if (!data) return <div style={S.page}><p style={{ color: 'var(--gss-danger)' }}>Erreur de chargement</p></div>;

  // Build 12-month revenue with gaps filled
  const rev12 = buildRevenue12(data.revenus_12mois || []);
  const maxRev = Math.max(...rev12.map(r => r.total), 1);

  // Teintes assombries : #1A8754 -> #146B43 (6,53:1), #D97706 -> #B45309 (5,02:1),
  // #DC2626 -> #C41E1E (5,91:1). Mesures WCAG sur fond blanc.
  const recouvrColor = data.taux_recouvrement >= 100 ? '#146B43' : data.taux_recouvrement < 80 ? '#C41E1E' : '#B45309';
  const impayeColor = data.impayes > 0 ? '#C41E1E' : '#146B43';

  return (
    <div style={S.page}>
      {/* ⚠️ `flexWrap` — sans lui, le titre et la barre de navigation se
          COMPRIMENT l'un l'autre au lieu de passer à la ligne : les six liens
          débordaient ou se chevauchaient sous ~700 px. Avec `wrap`, la barre
          descend sous le titre et reste entière. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <span className="gss-marque-produit">GSS-360</span>
          <div>
            <div style={{ fontSize: 11, color: 'var(--gss-red)', letterSpacing: 4, textTransform: 'uppercase' }}>Super Admin</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--gss-text)' }}>Tableau de bord SaaS</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <Link to="/super-admin/tenants" style={S.navBtn}>Tenants</Link>
          <Link to="/super-admin/clients-suivi" style={S.navBtn}>CRM</Link>
          <Link to="/super-admin/facturation" style={S.navBtn}>Facturation</Link>
          <Link to="/super-admin/tarifs" style={S.navBtn}>Tarifs</Link>
          <Link to="/super-admin/referentiel-pays" style={S.navBtn}>Referentiel pays</Link>
          <Link to="/super-admin/ia-config" style={S.navBtn}>Config IA</Link>
          <Link to="/super-admin/demandes" style={{ ...S.navBtn, position: 'relative' }}>
            Demandes
            {demandesNouv > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6,
                background: 'var(--gss-danger)', color: '#fff',
                fontSize: 9, fontWeight: 800, minWidth: 16, height: 16,
                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px',
              }}>{demandesNouv}</span>
            )}
          </Link>
          <button onClick={toggleTheme} title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}
            style={{ ...S.navBtn, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '6px 10px' }}>
            {theme === 'light' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            )}
          </button>
        </div>
      </div>

      {/* KPI Cards — 6 cards */}
      {/* ⚠️ 7 tuiles sur 6 colonnes laissaient « Impayés » ORPHELINE en deuxième
          ligne. `alignItems: stretch` + hauteur minimale dans KPI = hauteur uniforme.
          ⚠️ PLUS DE NOMBRE DE COLONNES FIXE. `repeat(4,1fr)` n'imposait pas une
             largeur en pixels, mais il imposait QUATRE COLONNES quelle que soit
             la place : à 390 px cela donnait quatre tuiles de ~85 px, illisibles.
             `auto-fit` laisse la GRILLE compter, pas le développeur.
          ⚠️ `min(100%, 240px)` — LE `min()` EST LE POINT ESSENTIEL. Un
             `minmax(240px, 1fr)` seul DÉBORDE dès que le conteneur est plus
             étroit que 240 px : la piste garde sa taille minimale et pousse la
             page. Avec `min(100%, …)`, la piste ne dépasse jamais son conteneur.
             C'est ce qui garantit l'absence de défilement horizontal À TOUTE
             LARGEUR, sans point de rupture à maintenir. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14, marginBottom: 24, alignItems: 'stretch' }}>
        {/* ⚠️ LE TOTAL ET LE CHIFFRE COMMERCIAL COEXISTENT — JAMAIS L'UN SANS
            L'AUTRE. Le 02/09, ce tableau affichait « MRR 205 000 » : c'etait
            FOX, une societe de l'editeur, facturee a elle-meme. UN FAUX CHIFFRE
            EST PIRE QU'UN ZERO — un zero se voit, un faux chiffre se croit.
            Le total ne disparait pas pour autant : on montre les deux, et la
            decomposition dit pourquoi ils different. */}
        <KPI label={t('Tenants')} value={data.tenants_actifs} color="#111111"
          perimetre={t('tous')}
          sub={`dont ${data.tenants_internes ?? 0} interne${(data.tenants_internes ?? 0) > 1 ? 's' : ''}`
            + ` · ${data.tenants_demo ?? 0} démonstration`} />
        {/* ═══════════════════════════════════════════════════════════════
            DEUX FAMILLES DE TUILES, CHACUNE NOMMEE (Dom, 04/09/2026)
            ═══════════════════════════════════════════════════════════════
            COMMERCIALES : excluent le groupe editeur et la demonstration.
            COMPTABLES   : incluent tout, groupe editeur compris.
            ⚠️ LE VRAI DEFAUT N'ETAIT PAS LE 205 000 : c'est que TROIS tuiles
               excluaient, DEUX non, et QU'AUCUNE NE LE DISAIT. Chaque tuile
               porte desormais son perimetre dans son libelle ou son sous-titre.
               Aucune ne laisse deviner. */}
        <KPI label={t('Clients facturables')} value={data.clients_facturables ?? 0}
          color={(data.clients_facturables ?? 0) > 0 ? '#146B43' : '#B45309'}
          perimetre={t('clients externes')}
          sub={t('hors groupe éditeur et démonstration')} />
        {/* ⚠️ AUCUN ACRONYME ANGLAIS VISIBLE A L'ECRAN (regle Dom, 03/09/2026) :
            le produit s'adresse a des dirigeants francophones. « MRR » ->
            « Revenu recurrent mensuel ».
            ⓘ « ARR » a été traduit le 04/09/2026 en « Revenu annuel ». Il était
              resté « en attente d'arbitrage » depuis le 03/09 — et je l'ai vu sur
              MA PROPRE capture, pas dans une relecture du code. Un acronyme
              anglais laissé « en attente » reste un acronyme anglais à l'écran. */}
        <KPI label={t('Revenu récurrent mensuel')} value={`${data.mrr_total.toLocaleString()} F`} color="var(--gss-red)"
          perimetre={t('clients externes')}
          sub={`Revenu annuel : ${data.arr_total.toLocaleString()} F`} />
        <KPI label={t('Nouveaux tenants')} value={data.nouveaux_tenants_mois} color="#1D4ED8"
          perimetre={t('ce mois')} sub={t('créés depuis le 1er du mois')} />
        {/* ⚠️ #A855F7 sur blanc = 3,54:1 — SOUS le seuil de 4,5. Assombri en
            #6D28D9 (7,26:1). Mesure WCAG, pas appreciation. */}
        <KPI label={t('Agents actifs')} value={data.agents_total} color="#6D28D9"
          perimetre={t('tous tenants')} sub={t('fiches non supprimées, statut actif')} />
        {/* ⚠️ UN TAUX SANS DENOMINATEUR N'EXISTE PAS. « 0 % » se lit
            « personne ne paie » ; « 100 % » se lit « tout est recouvre ».
            La verite quand rien n'est du, c'est « — ». */}
        <KPI label={t('Taux de recouvrement')}
          value={data.taux_recouvrement === null || data.taux_recouvrement === undefined
            ? '—' : `${data.taux_recouvrement}%`}
          color={data.taux_recouvrement === null || data.taux_recouvrement === undefined
            ? '#555555' : recouvrColor}
          perimetre={t('comptable')}
          sub={data.taux_recouvrement === null || data.taux_recouvrement === undefined
            ? t('aucune facture en cours') : t('factures annulées exclues')} />
        {/* ⚠️ TUILE COMPTABLE : elle INCLUT le groupe editeur, parce que FOX
            doit reellement payer. Mais le montant ne doit pas se lire comme un
            impaye CLIENT — d'ou la part intragroupe, dite sous le chiffre. */}
        <KPI label={t('Impayés')} value={`${data.impayes.toLocaleString()} F`} color={impayeColor}
          perimetre={t('tous tenants')}
          sub={(data.impayes_intragroupe ?? 0) > 0
            ? t('dont {n} F intragroupe', { n: Number(data.impayes_intragroupe).toLocaleString() })
            : t('factures annulées exclues')} />
      </div>

      {/* ⚠️ `2fr 1fr` imposait DEUX colonnes jusqu'à 0 px de large. Le graphique
          de revenus tombait à ~200 px sur un téléphone, ses douze barres à 15 px
          l'une. Ici le graphique réclame 360 px ; sous cette largeur, la grille
          passe d'elle-même à une colonne — c'est le point de rupture, exprimé en
          BESOIN du contenu plutôt qu'en largeur d'écran supposée. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Revenue chart */}
        <div style={S.card}>
          {/* ⚠️ « Revenus » laissait lire un chiffre d'affaires REALISE. Ce sont
              des montants EMIS. Le titre le dit maintenant, et la serie
              encaissee existe a cote — elle est CALCULEE (statut 'payee'),
              pas inventee. */}
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 4 }}>
            {t('Facturation emise — 12 derniers mois')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginBottom: 12 }}>
            {t('comptable — factures annulées exclues')}
            {(data.revenus_12mois || []).length === 0 && ' · ' + t('aucune facture émise sur la période')}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 160 }}>
            {rev12.map((r, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: 8, color: 'var(--gss-red)', marginBottom: 2 }}>
                  {Number(r.total) > 0 ? `${Math.round(r.total / 1000)}k` : ''}
                </div>
                <div style={{
                  width: '100%', background: Number(r.total) > 0 ? 'linear-gradient(180deg, var(--gss-red), var(--gss-red-dark))' : 'var(--gss-border)',
                  borderRadius: '4px 4px 0 0', height: `${Math.max((r.total / maxRev) * 130, 4)}px`,
                }} />
                <div style={{ fontSize: 9, color: 'var(--gss-text-light)', marginTop: 4 }}>{MOIS[r.mois - 1]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 5 clients */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 16 }}>{t('Abonnement mensuel par tenant')}</div>
          {(data.top_clients || []).map((c, i) => (
            <Link key={i} to="/super-admin/clients-suivi" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 0', borderBottom: '1px solid var(--gss-border)', textDecoration: 'none',
            }}>
              <span style={{ fontSize: 12, color: 'var(--gss-text-secondary)' }}>{c.nom}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ padding: '1px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700,
                  color: STATUT_COLORS[c.abo_statut] || 'var(--gss-text-light)',
                }}>{c.abo_statut || '-'}</span>
                <span style={{ fontSize: 12, color: 'var(--gss-red)', fontWeight: 700 }}>{Number(c.mrr_calcule).toLocaleString()} F</span>
              </span>
            </Link>
          ))}
          {(data.top_clients || []).length === 0 && <p style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>{t('Aucun abonnement')}</p>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
        {/* Alertes */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 12 }}>Alertes</div>
          {(data.alertes || []).length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--gss-success)' }}>Aucune alerte</div>
          ) : (
            data.alertes.map((a, i) => {
              const cfg = ALERTE_CFG[a.alerte_type] || { color: 'var(--gss-text-light)', bg: 'var(--gss-border)', label: a.alerte_type };
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--gss-border)' }}>
                  <span style={{ fontSize: 12, color: 'var(--gss-text-secondary)' }}>{a.nom}</span>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                    background: cfg.bg, color: cfg.color,
                  }}>{cfg.label}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Repartition par offre */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 12 }}>Repartition par offre</div>
          {(data.repartition_offres || []).length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>Aucun abonnement</div>
          ) : (
            data.repartition_offres.map((o, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--gss-border)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: OFFRE_COLORS[o.offre] || 'var(--gss-text-light)' }} />
                  <span style={{ fontSize: 12, color: 'var(--gss-text)', textTransform: 'uppercase', fontWeight: 600 }}>{OFFRE_LABELS[o.offre] || o.offre}</span>
                </span>
                <span style={{ fontSize: 14, color: 'var(--gss-text)', fontWeight: 700 }}>{o.nb}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * TUILE D'INDICATEUR — refondue le 04/09/2026 pour la LISIBILITÉ
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ CONTRASTE MESURÉ, PAS APPRÉCIÉ À L'ŒIL (WCAG 2.1, fond #FFFFFF) :
 *      AVANT  titre et sous-titre  #888888 →  3,54:1   ÉCHEC (seuil 4,5)
 *      APRÈS  titre    #333333     → 12,63:1   OK
 *             étiquette et sous-titre #555555 →  7,46:1   OK
 *    Le public lit souvent sur un téléphone en plein soleil : le gris clair
 *    sur blanc y est invisible. 3,54:1 n'était pas « un peu juste », c'était
 *    sous le seuil.
 *
 * ⚠️ LE PÉRIMÈTRE SORT DU TITRE. « TAUX DE RECOUVREMENT — CLIENTS EXTERNES »
 *    tenait sur trois lignes en majuscules grises. Les majuscules ralentissent
 *    la lecture et cassent les mots longs. Le périmètre reste affiché — il
 *    devient une PASTILLE, le titre redevient une phrase.
 *
 * ⚠️ HAUTEUR UNIFORME : le titre occupe une hauteur fixe, quel que soit son
 *    nombre de lignes, sinon les chiffres ne s'alignent plus d'une tuile à
 *    l'autre et l'œil ne peut plus les comparer.
 */
function KPI({ label, value, color, sub, perimetre }) {
  return (
    <div style={{ ...S.card, display: 'flex', flexDirection: 'column', minHeight: 132 }}>
      <div style={{
        fontSize: 14, fontWeight: 600, color: '#333333', lineHeight: 1.25,
        minHeight: 36, marginBottom: 6,
      }}>{label}</div>
      {perimetre && (
        <div style={{
          alignSelf: 'flex-start', fontSize: 11, color: '#555555',
          background: '#F4F4F5', borderRadius: 5, padding: '2px 8px', marginBottom: 8,
          whiteSpace: 'nowrap',
        }}>{perimetre}</div>
      )}
      <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1.1, marginTop: 'auto' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#555555', marginTop: 6, lineHeight: 1.35 }}>{sub}</div>}
    </div>
  );
}

function buildRevenue12(raw) {
  const now = new Date();
  const result = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mois = d.getMonth() + 1;
    const annee = d.getFullYear();
    const found = raw.find(r => Number(r.mois) === mois && Number(r.annee) === annee);
    result.push({ mois, annee, total: found ? Number(found.total) : 0 });
  }
  return result;
}

const S = {
  // ⚠️ `padding: 28` fixe mangeait 56 px sur 390 — 14 % de la largeur, pris sur
  //    le contenu. `clamp` le fait respirer sans jamais l'écraser : 12 px sur un
  //    téléphone, 28 px dès qu'il y a la place.
  // ⚠️ PAS D'`overflowX: hidden` ICI. Masquer un débordement le rend invisible
  //    sans le supprimer : le contenu est COUPÉ, et personne ne sait ce qui
  //    manque. Si quelque chose déborde encore, il doit SE VOIR.
  page: { width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: 'var(--gss-bg)', padding: 'clamp(12px, 3vw, 28px)' },
  card: { background: 'var(--gss-white)', borderRadius: 12, padding: 20, border: '1px solid var(--gss-border)', minWidth: 0 },
  navBtn: {
    padding: '8px 16px', borderRadius: 8, background: 'var(--gss-white)', color: 'var(--gss-red)',
    textDecoration: 'none', fontSize: 12, fontWeight: 600, border: '1px solid var(--gss-border-strong)',
  },
};
